import Result from "@Core/Result";
import Axios from "axios";
import { IRequestOptions } from "@Core/ServiceBase";
import { IServiceUser } from "@Core/session/IServiceUser";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { transformUrl } from "@Utils";

/**
 * User's session context manager.
 */
class SessionManager {
    private static _isInitialized = false;

    /**
     * Reset the user's session.
     */
    public static resetSession(): void {
        this._isInitialized = false;
    }

    /**
     * Refresh the access token.
     */
    public static refreshToken = async (failedRequest) => {
        const serviceUser = JSON.parse(localStorage.getItem("serviceUser"));
        const refreshToken = serviceUser?.refreshToken;
        const payload = refreshToken ? { Value: refreshToken } : {};

        const axiosResult = await Axios.post(transformUrl(`/api/Account/RefreshToken`), payload);
        const result = new Result(axiosResult?.data.value, ...axiosResult?.data.errors);

        if (result.hasErrors) {
            SessionManager.deleteServiceUser();
            location.replace(transformUrl("/"));
            return Promise.reject("Refresh token could not be validated, Login required.");
        } else {
            serviceUser.accessToken = result.value;
            SessionManager.setServiceUser(serviceUser);
            failedRequest.response.config.headers["Authorization"] = "Bearer " + result.value;
            return Promise.resolve();
        }
    };

    /**
     * Initialise the session object.
     */
    public static initSession(): void {
        if (this._isInitialized) {
            throw Error("SessionManager: already initialized.");
        }

        this._isInitialized = true;

        // Use interceptor to refresh token on 401.
        createAuthRefreshInterceptor(Axios, this.refreshToken);

        // Use interceptor to inject the token to requests.
        Axios.interceptors.request.use((request) => {
            request.headers["Authorization"] = `Bearer ${SessionManager.getServiceUser()?.accessToken}`;
            return request;
        });
    }

    /**
     * Get the service user from local storage.
     */
    public static getServiceUser(): IServiceUser {
        return JSON.parse(window.localStorage.getItem("serviceUser")) as IServiceUser;
    }

    /**
     * Set the service user in local storage.
     */
    public static setServiceUser(serviceUser: IServiceUser) {
        window.localStorage.setItem("serviceUser", JSON.stringify(serviceUser));
    }

    /**
     * Delete the service user in local storage.
     */
    public static deleteServiceUser() {
        window.localStorage.removeItem("serviceUser");
    }

    /**
     * Return true is service user access token exists.
     */
    public static get isAuthenticated(): boolean {
        return this.getServiceUser()?.accessToken != null;
    }

    /**
     * Make request with JSON data.
     * @param opts
     */
    public static async requestJson<T>({ url: urlSlug, ...opts }: IRequestOptions): Promise<Result<T>> {
        const url = opts.originUrl ? `${opts.originUrl}${urlSlug}` : transformUrl(urlSlug);

        return await Axios.request({ url, ...opts })
            .then((axiosResult) => {
                const value = axiosResult?.data?.value ?? axiosResult?.data;

                return new Result(value, ...(axiosResult?.data?.errors ?? []));
            })
            .catch((error) => {
                if (urlSlug.includes("/Account/Upload")) {
                    return new Result(null, error.response.data);
                } else {
                    return new Result(null, error.message);
                }
            });
    }
}
export default SessionManager;
